/* Admin → Find Slot & Waitlist (Phase B)
   Smart search with structured filters (course/teacher/day/time/duration) returns
   slot cards with status badges. Side tab for Waitlist CRUD. */
import { useState } from "react";
import api, { useList, errMsg, fmtRM, DAYS } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, Plus, Trash2, CheckCircle2, Clock, ListChecks, UserPlus, ArrowRight } from "lucide-react";

const STATUS_COLORS = {
  available_now: "bg-emerald-100 text-emerald-800 border-emerald-200",
  available_from_date: "bg-blue-100 text-blue-800 border-blue-200",
  temporarily_blocked: "bg-amber-100 text-amber-800 border-amber-200",
  occupied: "bg-rose-100 text-rose-800 border-rose-200",
  waitlist_only: "bg-purple-100 text-purple-800 border-purple-200",
  potential_opening_soon: "bg-indigo-100 text-indigo-800 border-indigo-200",
};
const STATUS_LABELS = {
  available_now: "Available now",
  available_from_date: "Available from date",
  temporarily_blocked: "Temporarily blocked",
  occupied: "Occupied",
  waitlist_only: "Waitlist only",
  potential_opening_soon: "Opening soon",
};

export default function FindSlotPage() {
  return (
    <div data-testid="find-slot-page">
      <PageHeader title="Find a slot · Waitlist"
        description="Smart search + waitlist for admin and front desk." />
      <Tabs defaultValue="search">
        <TabsList>
          <TabsTrigger value="search" data-testid="tab-search">
            <Search className="h-3.5 w-3.5 mr-1" />Slot search
          </TabsTrigger>
          <TabsTrigger value="waitlist" data-testid="tab-waitlist">
            <ListChecks className="h-3.5 w-3.5 mr-1" />Waitlist
          </TabsTrigger>
        </TabsList>
        <TabsContent value="search"><SlotSearch /></TabsContent>
        <TabsContent value="waitlist"><WaitlistTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function SlotSearch() {
  const { data: courses } = useList("/courses");
  const { data: teachers } = useList("/teachers");
  const { data: studios } = useList("/studios");
  const [f, setF] = useState({
    course_id: "", teacher_id: "", day_of_week: "", date: "",
    time_from: "", time_to: "", duration_minutes: 45, studio_id: "", only_available: true,
  });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const payload = { ...f,
        course_id: f.course_id || null, teacher_id: f.teacher_id || null,
        day_of_week: f.day_of_week === "" ? null : parseInt(f.day_of_week, 10),
        date: f.date || null, time_from: f.time_from || null, time_to: f.time_to || null,
        studio_id: f.studio_id || null,
        duration_minutes: parseInt(f.duration_minutes, 10) || 45,
      };
      const r = await api.post("/admin/availability-search", payload);
      setResults(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 mt-4">
      <div className="rounded-lg border bg-card p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Course</Label>
          <Select value={f.course_id || "_any"} onValueChange={(v) => setF({ ...f, course_id: v === "_any" ? "" : v })}>
            <SelectTrigger data-testid="search-course"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">Any course</SelectItem>
              {(courses || []).filter(c => c.status !== "inactive").map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Teacher</Label>
          <Select value={f.teacher_id || "_any"} onValueChange={(v) => setF({ ...f, teacher_id: v === "_any" ? "" : v })}>
            <SelectTrigger data-testid="search-teacher"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">Any teacher (matching course)</SelectItem>
              {(teachers || []).map(t => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Day</Label>
          <Select value={f.day_of_week === "" ? "_any" : String(f.day_of_week)}
                  onValueChange={(v) => setF({ ...f, day_of_week: v === "_any" ? "" : v })}>
            <SelectTrigger data-testid="search-day"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">Any day</SelectItem>
              {DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Time from</Label>
          <Input type="time" value={f.time_from} onChange={e => setF({ ...f, time_from: e.target.value })} data-testid="search-time-from" />
        </div>
        <div>
          <Label>Time to</Label>
          <Input type="time" value={f.time_to} onChange={e => setF({ ...f, time_to: e.target.value })} data-testid="search-time-to" />
        </div>
        <div>
          <Label>Duration (mins)</Label>
          <Input type="number" value={f.duration_minutes} onChange={e => setF({ ...f, duration_minutes: e.target.value })} data-testid="search-duration" />
        </div>
        <div>
          <Label>Studio</Label>
          <Select value={f.studio_id || "_any"} onValueChange={(v) => setF({ ...f, studio_id: v === "_any" ? "" : v })}>
            <SelectTrigger data-testid="search-studio"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">Any studio</SelectItem>
              {(studios || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Specific date (optional)</Label>
          <Input type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} data-testid="search-date" />
        </div>
        <div className="flex items-center gap-2 pt-6">
          <Switch checked={f.only_available} onCheckedChange={(v) => setF({ ...f, only_available: v })} data-testid="search-only-available" />
          <Label className="cursor-pointer text-xs">Available only</Label>
        </div>
        <div className="md:col-span-3 flex justify-end">
          <Button onClick={run} disabled={busy} data-testid="search-run">
            <Search className="h-4 w-4 mr-1" />{busy ? "Searching…" : "Search slots"}
          </Button>
        </div>
      </div>

      {results && (
        <div data-testid="search-results">
          <p className="text-sm font-semibold mb-3">{results.total_found} result{results.total_found !== 1 ? "s" : ""} {results.message && `· ${results.message}`}</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {results.results.map((r, i) => (
              <div key={i} className="rounded-lg border bg-card p-4 flex items-start justify-between gap-3" data-testid={`result-card-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">
                    {DAYS[r.day_of_week]} {r.date} · {r.start_time}–{r.end_time}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Teacher <b className="text-foreground">{r.teacher_name}</b> · Studio <b className="text-foreground">{r.studio_name}</b> · {r.duration_minutes} mins
                  </p>
                  <span className={`inline-block mt-2 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${STATUS_COLORS[r.slot_status] || "bg-muted"}`}>
                    {STATUS_LABELS[r.slot_status] || r.slot_status}
                  </span>
                  {r.available_from_date && (
                    <p className="text-xs text-blue-700 mt-1">↪ Available from <b>{r.available_from_date}</b></p>
                  )}
                  {r.internal_admin_remark && (
                    <p className="text-[11px] text-muted-foreground italic mt-1">{r.internal_admin_remark}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-muted-foreground uppercase font-semibold mb-1">Recommended</p>
                  <p className="text-xs font-semibold text-secondary">{r.recommended_action}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WaitlistTab() {
  const { data: courses } = useList("/courses");
  const { data: teachers } = useList("/teachers");
  const { data: waitlist, reload } = useList("/admin/waitlist");
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({
    parent_name: "", parent_phone: "", parent_email: "", student_name: "", student_age: "",
    course_id: "", preferred_teacher_id: "", preferred_day: "", preferred_time_from: "",
    desired_start_date: "", admin_remark: "", follow_up_date: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.parent_name || !f.parent_phone || !f.student_name || !f.course_id) {
      toast.error("Parent name, phone, student name and course are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/waitlist", {
        ...f,
        preferred_teacher_id: f.preferred_teacher_id || null,
        preferred_day: f.preferred_day === "" ? null : parseInt(f.preferred_day, 10),
      });
      toast.success("Added to waitlist");
      setAdding(false); reload();
      setF({ parent_name: "", parent_phone: "", parent_email: "", student_name: "", student_age: "",
        course_id: "", preferred_teacher_id: "", preferred_day: "", preferred_time_from: "",
        desired_start_date: "", admin_remark: "", follow_up_date: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const update = async (id, body) => {
    try { await api.put(`/admin/waitlist/${id}`, body); reload(); toast.success("Updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Remove this waitlist entry?")) return;
    try { await api.delete(`/admin/waitlist/${id}`); reload(); toast.success("Removed"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const STATUS_STYLES = {
    interested: "bg-blue-100 text-blue-800",
    waiting: "bg-amber-100 text-amber-800",
    contacted: "bg-indigo-100 text-indigo-800",
    converted: "bg-emerald-100 text-emerald-800",
    closed: "bg-muted text-muted-foreground",
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end">
        <Button onClick={() => setAdding(true)} data-testid="add-waitlist-button">
          <Plus className="h-4 w-4 mr-1" />Add to waitlist
        </Button>
      </div>
      {adding && (
        <div className="rounded-lg border bg-card p-5 space-y-3" data-testid="waitlist-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Parent name *</Label><Input value={f.parent_name} onChange={e => setF({ ...f, parent_name: e.target.value })} data-testid="wl-parent-name" /></div>
            <div><Label>Parent phone *</Label><Input value={f.parent_phone} onChange={e => setF({ ...f, parent_phone: e.target.value })} data-testid="wl-parent-phone" /></div>
            <div><Label>Parent email</Label><Input type="email" value={f.parent_email} onChange={e => setF({ ...f, parent_email: e.target.value })} data-testid="wl-parent-email" /></div>
            <div><Label>Student name *</Label><Input value={f.student_name} onChange={e => setF({ ...f, student_name: e.target.value })} data-testid="wl-student-name" /></div>
            <div>
              <Label>Course *</Label>
              <Select value={f.course_id} onValueChange={(v) => setF({ ...f, course_id: v })}>
                <SelectTrigger data-testid="wl-course"><SelectValue placeholder="Pick course" /></SelectTrigger>
                <SelectContent>{(courses || []).filter(c => c.status !== "inactive").map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Preferred teacher</Label>
              <Select value={f.preferred_teacher_id || "_any"} onValueChange={(v) => setF({ ...f, preferred_teacher_id: v === "_any" ? "" : v })}>
                <SelectTrigger data-testid="wl-teacher"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_any">Any teacher</SelectItem>
                  {(teachers || []).map(t => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Preferred day</Label>
              <Select value={f.preferred_day === "" ? "_any" : String(f.preferred_day)} onValueChange={(v) => setF({ ...f, preferred_day: v === "_any" ? "" : v })}>
                <SelectTrigger data-testid="wl-day"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_any">Any day</SelectItem>
                  {DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Preferred time</Label><Input type="time" value={f.preferred_time_from} onChange={e => setF({ ...f, preferred_time_from: e.target.value })} data-testid="wl-time" /></div>
            <div><Label>Desired start date</Label><Input type="date" value={f.desired_start_date} onChange={e => setF({ ...f, desired_start_date: e.target.value })} data-testid="wl-start-date" /></div>
            <div><Label>Follow-up date</Label><Input type="date" value={f.follow_up_date} onChange={e => setF({ ...f, follow_up_date: e.target.value })} data-testid="wl-follow-up" /></div>
            <div className="md:col-span-2"><Label>Admin remark</Label><Textarea value={f.admin_remark} onChange={e => setF({ ...f, admin_remark: e.target.value })} data-testid="wl-remark" /></div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="wl-save">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      )}

      {!waitlist ? <Spinner /> : waitlist.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-12">No waitlist entries yet.</p>
      ) : (
        <div className="rounded-lg border overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide">
              <tr><th className="text-left p-3">Student</th><th className="text-left p-3">Course</th><th className="text-left p-3">Preferred</th><th className="text-left p-3">Follow-up</th><th className="text-left p-3">Status</th><th></th></tr>
            </thead>
            <tbody>
              {waitlist.map(w => (
                <tr key={w.id} className="border-t" data-testid={`waitlist-row-${w.id}`}>
                  <td className="p-3">
                    <p className="font-medium">{w.student_name}</p>
                    <p className="text-xs text-muted-foreground">{w.parent_name} · {w.parent_phone}</p>
                  </td>
                  <td className="p-3">{w.course_name}</td>
                  <td className="p-3 text-xs">
                    {w.preferred_teacher_name && <p>{w.preferred_teacher_name}</p>}
                    {Array.isArray(w.preferred_time_choices) && w.preferred_time_choices.length > 0 ? (
                      <div className="space-y-0.5">
                        {w.preferred_time_choices.map((c, i) => (
                          <p key={i} data-testid={`wl-choice-${w.id}-${i}`}>
                            <b>{i + 1}.</b> {DAYS[c.day_of_week]} {c.time_from}–{c.time_to}
                          </p>
                        ))}
                        {w.source_channel === "website" && <p className="text-[10px] text-secondary italic">via website</p>}
                      </div>
                    ) : (
                      w.preferred_day != null && <p>{DAYS[w.preferred_day]} {w.preferred_time_from || ""}</p>
                    )}
                  </td>
                  <td className="p-3 text-xs">{w.follow_up_date || "—"}</td>
                  <td className="p-3">
                    <Select value={w.status} onValueChange={(v) => update(w.id, { status: v })}>
                      <SelectTrigger className={`w-32 ${STATUS_STYLES[w.status] || ""}`} data-testid={`wl-status-${w.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["interested", "waiting", "contacted", "converted", "closed"].map(s =>
                          <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => remove(w.id)} data-testid={`wl-delete-${w.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
